import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import { ConfigurationType } from 'data/types';
import { setConfigurations } from 'features/configurations';
import { produce } from 'immer';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { hasCurrentAirHandlerPool } from 'utils/air-handler-availability';
import {
  condenserHasAnyValidSystem,
  condenserMatchingSystemCount
} from 'utils/air-handler-pool-tuples';
import {
  findZoneNameByUniqueId,
  markCondensersStale
} from 'utils/configurations';
import { describeRadialDuctFailReasons } from 'utils/radial-duct-fail-messages';
import { findSpaceById } from 'utils/structure';

export type CdSnapshotMetaType =
  | { source: 'air-handler-move'; zoneUniqueId: string }
  | { source: 'duct-run-edit'; zoneUniqueId: string }
  | { source: 'duct-endpoint' };

let pendingSnapshot: ConfigurationType | null = null;
let pendingSnapshotMeta: CdSnapshotMetaType | null = null;

export function captureCdSnapshot(
  prevConfig: ConfigurationType,
  meta: CdSnapshotMetaType = { source: 'duct-endpoint' }
): void {
  pendingSnapshot = structuredClone(prevConfig);
  pendingSnapshotMeta = meta;
}

const REVERT_TOAST_ID = 'cd-revert-toast';

type RunRemovalCandidateType = {
  zoneUniqueId: string;
  spaceIds: string[];
};

export default function ConcealedDuctRevertWatcher() {
  const dispatch = useAppDispatch();
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const activeConfiguration = configurations[activeConfig];

  const lastDecidedSnapshotRef = useRef<ConfigurationType | null>(null);

  useEffect(() => {
    if (!pendingSnapshot || !activeConfiguration) return;
    if (lastDecidedSnapshotRef.current === pendingSnapshot) return;

    const cdCondensers = activeConfiguration.condensers.filter((c) =>
      c.zones.some(
        (z) =>
          z.airHandler?.airHandlerTypeCode === 'concealed' ||
          z.airHandler?.airHandlerTypeCode === 'concealed_high_static'
      )
    );

    if (cdCondensers.length === 0) {
      pendingSnapshot = null;
      return;
    }

    // The options request owns availability during the air-handler step: a
    // concealed edit marks the pool stale and the decision waits for the
    // fresh pool (the full rows only arrive once the step is complete).
    if (
      cdCondensers.some(
        (c) => !hasCurrentAirHandlerPool(c) && !c.validSystemsFetched
      )
    ) {
      return;
    }

    const failing = cdCondensers.filter((c) => !condenserHasAnyValidSystem(c));

    const snapshot = pendingSnapshot;
    const meta = pendingSnapshotMeta;
    lastDecidedSnapshotRef.current = snapshot;
    pendingSnapshot = null;
    pendingSnapshotMeta = null;

    const handleRevert = () => {
      const latest = store.getState().configurations.configurations;
      const current = latest.map((cfg) =>
        cfg.uniqueId === snapshot.uniqueId ? snapshot : cfg
      );
      const condenserIds = snapshot.condensers.map((c) => c.uniqueId);
      const reverted = current.map((cfg) =>
        cfg.uniqueId === snapshot.uniqueId
          ? markCondensersStale(cfg, condenserIds)
          : cfg
      );
      dispatch(setConfigurations(reverted));
    };

    const isAirHandlerMove = meta?.source === 'air-handler-move';
    const zoneScopedMeta =
      meta && meta.source !== 'duct-endpoint' ? meta : null;

    if (failing.length === 0) {
      if (!zoneScopedMeta) return;

      const affectedCondenser = activeConfiguration.condensers.find((c) =>
        c.zones.some((z) => z.uniqueId === zoneScopedMeta.zoneUniqueId)
      );
      if (!affectedCondenser) return;
      const snapshotCondenser = snapshot.condensers.find(
        (c) => c.uniqueId === affectedCondenser.uniqueId
      );
      const postMatching = condenserMatchingSystemCount(affectedCondenser);
      const preMatching = snapshotCondenser
        ? condenserMatchingSystemCount(snapshotCondenser)
        : 0;
      if (postMatching > 0 || preMatching === 0) return;

      const affectedZoneName =
        findZoneNameByUniqueId(
          activeConfiguration,
          zoneScopedMeta.zoneUniqueId
        ) ?? 'this zone';
      const message = isAirHandlerMove
        ? 'This air handler position leaves no matching air handler systems — only options with other air handler types remain.'
        : 'This duct layout leaves no matching air handler systems — only options with other air handler types remain.';
      const hint = isAirHandlerMove
        ? `Alternatively, keep this position, add another condenser, and move ${affectedZoneName} to it.`
        : `Reverting restores the last working duct layout for ${affectedZoneName}.`;
      const revertLabel = isAirHandlerMove ? 'Revert move' : 'Revert change';
      const keepLabel = isAirHandlerMove ? 'Keep position' : 'Keep change';
      toast(
        (t) => (
          <div className='flex flex-col gap-2'>
            <span>{message}</span>
            <span className='text-sm text-gray-600'>{hint}</span>
            <div className='flex items-center gap-2'>
              <button
                type='button'
                className='rounded bg-amber-600 px-2 py-1 text-sm font-medium text-white hover:bg-amber-700'
                onClick={() => {
                  handleRevert();
                  toast.dismiss(t.id);
                }}
              >
                {revertLabel}
              </button>
              <button
                type='button'
                className='rounded bg-white px-2 py-1 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
                onClick={() => {
                  toast.dismiss(t.id);
                }}
              >
                {keepLabel}
              </button>
            </div>
          </div>
        ),
        { id: REVERT_TOAST_ID, duration: Infinity }
      );
      return;
    }

    const failReasons = failing.flatMap((c) =>
      Object.values(c.ductingSummary ?? {})
        .filter((zone) => !zone.pass)
        .flatMap((zone) => zone.reasons)
    );
    const description = describeRadialDuctFailReasons(failReasons);

    const removalCandidates: RunRemovalCandidateType[] = failing.flatMap((c) =>
      Object.entries(c.ductingSummary ?? {})
        .filter(
          ([, zone]) =>
            !zone.pass && (zone.removableRunSpaceIds?.length ?? 0) > 0
        )
        .map(([zoneUniqueId, zone]) => ({
          zoneUniqueId,
          spaceIds: zone.removableRunSpaceIds ?? []
        }))
    );
    const failingCondenserIds = failing.map((c) => c.uniqueId);
    const configUniqueId = activeConfiguration.uniqueId;

    const handleRemoveRuns = () => {
      const latest = store.getState().configurations.configurations;
      const updated = produce(latest, (draft) => {
        const config = draft.find((c) => c.uniqueId === configUniqueId);
        if (!config) return;
        for (const condenser of config.condensers) {
          for (const zone of condenser.zones) {
            const candidate = removalCandidates.find(
              (r) => r.zoneUniqueId === zone.uniqueId
            );
            if (candidate) {
              for (const item of zone.zoneItems) {
                if (candidate.spaceIds.includes(item.structureItemId)) {
                  item.servePassively = true;
                }
              }
              zone.modified = true;
              config.modified = true;
            }
          }
        }
      }).map((cfg) =>
        cfg.uniqueId === configUniqueId
          ? markCondensersStale(cfg, failingCondenserIds)
          : cfg
      );
      dispatch(setConfigurations(updated));
    };

    const revertNoun = isAirHandlerMove ? 'Move' : 'Change';
    if (removalCandidates.length === 0) {
      handleRevert();
      toast.error(
        description
          ? `${revertNoun} reverted — ${description}.`
          : `${revertNoun} reverted — no valid systems with this layout.`,
        { id: REVERT_TOAST_ID, duration: 10000 }
      );
      return;
    }

    const { structure } = store.getState().structure;
    const roomLabels = removalCandidates
      .flatMap((r) => r.spaceIds)
      .map((id) => findSpaceById(structure, id)?.label ?? 'a room');
    const runLabel =
      roomLabels.length === 1
        ? `the supply run to ${roomLabels[0]}`
        : `${roomLabels.length} supply runs (${roomLabels.join(', ')})`;

    const keptNoun = isAirHandlerMove ? 'position' : 'layout';
    toast(
      (t) => (
        <div className='flex flex-col gap-2'>
          <span>
            This {keptNoun} only works without {runLabel}
            {description ? ` — ${description}` : ''}.
          </span>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              className='rounded bg-amber-600 px-2 py-1 text-sm font-medium text-white hover:bg-amber-700'
              onClick={() => {
                handleRemoveRuns();
                toast.dismiss(t.id);
              }}
            >
              Remove run & keep {keptNoun}
            </button>
            <button
              type='button'
              className='rounded bg-white px-2 py-1 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
              onClick={() => {
                handleRevert();
                toast.dismiss(t.id);
              }}
            >
              {isAirHandlerMove ? 'Revert move' : 'Revert change'}
            </button>
          </div>
        </div>
      ),
      { id: REVERT_TOAST_ID, duration: Infinity }
    );
  }, [configurations, activeConfiguration, dispatch]);

  return null;
}
